import React from 'react';
import styled from '@emotion/styled';
import { PhoneFilledIcon } from './header-mobile-old/Icons/PhoneFilledIcon';
import { __ } from '../../../utils/translation-service';
import { LocaleConfigContext } from '../../../contexts/locale-config/locale-config';

interface Props {
    phoneNumber: string;
    contactPagePath: string;
    contactTargetBlank?: boolean;
    firstLine: string;
    secondLine?: string;
    hideSeparator?: boolean;
}

const HeaderContactWrapper = styled.div`
    position: relative;
    display: flex;
    height: 46px;
    text-align: left;
    justify-content: flex-end;
    align-items: center;
    ${(props) => props.theme.media.mdAndUp} {
        height: 90px;
    }
`;

const HeaderContactPhoneWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 0;
    cursor: pointer;
    &:hover {
        address {
            display: none;
            ${(props) => props.theme.media.smAndUp} {
                display: block;
            }
        }
    }
`;

const HeaderContactPhoneTitle = styled.div`
    color: ${({ theme }) => theme.headerContacts.titleColor};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    display: none;
    ${(props) => props.theme.media.mdAndUp} {
        display: block;
    }
`;
const HeaderContactPhoneTitleArrow = styled.div`
    position: relative;
    display: inline-block;
    width: 14px;
    height: 12px;
    margin-left: 5px;
    &:after,
    &:before {
        content: '';
        position: absolute;
        top: 8px;
        width: 9px;
        height: 2px;
        border-radius: 2px;
        background-color: ${({ theme }) => theme.headerContacts.titleColor};
    }
    &:after {
        right: 0;
        transform: rotate(-45deg);
    }
    &:before {
        left: 0;
        transform: rotate(45deg);
    }
`;

const HeaderContactPhone = styled.div`
    color: ${({ theme }) => theme.headerContacts.phoneColor};
    font-size: 24px;
    font-weight: 700;
    display: none;
    ${(props) => props.theme.media.mdAndUp} {
        display: block;
    }
`;

const HeaderContactInfo = styled.address`
    display: none;
    position: absolute;
    top: 50px;
    right: 0;
    padding: 16px;
    background-color: #fff;
    border-radius: 6px;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.12);
    z-index: 2;
    cursor: default;
    display: none;
    ${(props) => props.theme.media.mdAndUp} {
        top: 75px;
    }

    > p {
        margin-top: 0;
        margin-bottom: 16px;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const HeaderContactInfoTitle = styled.div`
    color: ${({ theme }) => theme.colorsUnifications.gunmetal[100]};
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 6px;
    white-space: nowrap;
    > span {
        font-weight: 400;
    }
`;

const HeaderContactInfoText = styled.p`
    color: ${({ theme }) => theme.colorsUnifications.gunmetal[100]};
    font-size: 14px;
    white-space: nowrap;
`;

const HeaderContactInfoLink = styled.a`
    color: ${({ theme }) => theme.link.primary};
    font-size: 14px;
    font-weight: 700;
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
        text-decoration: none;
    }
`;

const PhoneIconWrapper = styled.div`
    height: 20px;
`;

const PhoneIconStyled = styled(PhoneFilledIcon)`
    fill: ${({ theme }) => theme.icon.brandColor1};
    stroke: ${({ theme }) => theme.icon.brandColor1};
    margin-right: 0;
    ${(props) => props.theme.media.mdAndUp} {
        margin-right: 16px;
    }
`;

const HeaderContactSeparator = styled.div`
    height: 28px;
    width: 0;
    margin: 0 12px;
    background-color: ${({ theme }) => theme.colorsUnifications.seashell[100]};
    ${(props) => props.theme.media.mdAndUp} {
        width: 2px;
        margin: 0 16px;
    }
`;

export const HeaderContactBlock = ({
    phoneNumber,
    contactPagePath,
    contactTargetBlank,
    firstLine,
    secondLine,
    hideSeparator,
}: Props) => {
    const localeConfig = React.useContext(LocaleConfigContext);
    const { marketMap } = localeConfig;
    const { isCruise1stAU, isCruise1stUK, isDreamlinesNL } = marketMap;
    const isCruise1st = isCruise1stAU || isCruise1stUK;

    const afterSalePhoneUK = '0161 385 7425'; // should be replaced with the data from API

    return (
        <>
            <HeaderContactWrapper>
                {!hideSeparator && <HeaderContactSeparator />}
                <PhoneIconWrapper>
                    <PhoneIconStyled width={20} height={20} />
                </PhoneIconWrapper>
                <HeaderContactPhoneWrapper>
                    <HeaderContactPhoneTitle>
                        {__('headerContacts.openDailyForExpertAdvice')}
                        <HeaderContactPhoneTitleArrow />
                    </HeaderContactPhoneTitle>
                    <HeaderContactPhone
                        dangerouslySetInnerHTML={{ __html: phoneNumber }}
                        data-test-id="header-phone"
                    />
                    <HeaderContactInfo>
                        <HeaderContactInfoTitle>
                            {__('headerContacts.sales')}:
                        </HeaderContactInfoTitle>
                        <HeaderContactInfoText dangerouslySetInnerHTML={{ __html: firstLine }} />
                        {secondLine && (
                            <>
                                {!isCruise1stUK && !isDreamlinesNL && (
                                    <HeaderContactInfoTitle>
                                        {__('headerContacts.afterSales')}:
                                    </HeaderContactInfoTitle>
                                )}
                                {isCruise1stUK && (
                                    <HeaderContactInfoTitle>
                                        {__('headerContacts.alreadyBooked')}
                                        <br />
                                        <span>{__('headerContacts.callAftersales')}</span>{' '}
                                        {afterSalePhoneUK}
                                    </HeaderContactInfoTitle>
                                )}
                                <HeaderContactInfoText
                                    dangerouslySetInnerHTML={{ __html: secondLine }}
                                />
                            </>
                        )}
                        {!isCruise1st && (
                            <HeaderContactInfoLink
                                href={contactPagePath}
                                target={contactTargetBlank ? '_blank' : '_self'}
                            >
                                {__('headerContacts.chooseCruiseExpert')}
                            </HeaderContactInfoLink>
                        )}
                    </HeaderContactInfo>
                </HeaderContactPhoneWrapper>
            </HeaderContactWrapper>
        </>
    );
};
